import { LitElement, html, css, unsafeCSS } from 'lit';
import { THEMES_CSS, SHARED_CSS } from './themes.js';
import { fetchStandings, translateCountry } from './utils.js';

export class QghStandings extends LitElement {
  static styles = [
    unsafeCSS(THEMES_CSS + SHARED_CSS),
    css`
      :host {
        display: block;
        font-family: 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        background: var(--qgh-bg);
        border-radius: var(--qgh-radius);
        min-width: 0; max-width: 100%;
      }
      .widget { background: var(--qgh-bg); color: var(--qgh-color); border-radius: var(--qgh-radius); overflow: hidden; min-width: 0; }
      .header { padding: 12px 14px 0; min-width: 0; }
      .header-title { font-size: 13px; font-weight: 700; margin-bottom: 10px; }
      .tabs { display: flex; gap: 6px; overflow-x: auto; padding-bottom: 10px; scrollbar-width: thin; scrollbar-color: var(--qgh-border) transparent; }
      .tabs::-webkit-scrollbar { height: 3px; }
      .tabs::-webkit-scrollbar-track { background: transparent; }
      .tabs::-webkit-scrollbar-thumb { background: var(--qgh-border); border-radius: 2px; }
      .tab {
        flex-shrink: 0; padding: 4px 12px; border-radius: 20px; border: none; cursor: pointer;
        font-size: 11px; font-weight: 700; letter-spacing: .05em;
        background: var(--qgh-nav-bg); color: var(--qgh-color); transition: background .15s;
      }
      .tab:hover { background: var(--qgh-nav-hover); }
      .tab.active { background: var(--qgh-accent); color: #000; }
      table { width: 100%; border-collapse: collapse; }
      thead th {
        padding: 6px 4px; text-align: center;
        font-size: 9px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase;
        opacity: .4; border-bottom: 1px solid var(--qgh-border);
      }
      thead th.left { text-align: left; }
      thead th:first-child { padding-left: 12px; }
      tbody tr { border-bottom: 1px solid var(--qgh-border); }
      tbody tr:last-child { border-bottom: none; }
      tbody td { padding: 7px 4px; text-align: center; vertical-align: middle; font-size: 12px; }
      td.rank { opacity: .5; padding-left: 10px; width: 22px; font-size: 11px; }
      td.team-col { text-align: left; padding-left: 0; }
      .team-cell { display: flex; align-items: center; gap: 8px; }
      .team-logo { width: 18px; height: 18px; object-fit: contain; flex-shrink: 0; }
      td.pts { font-weight: 700; color: var(--qgh-accent); padding-right: 10px; }
      .qualify  { border-left: 3px solid var(--qgh-accent); }
      .nqualify { border-left: 3px solid transparent; }
      .qualify-note {
        display: flex; align-items: center; gap: 6px;
        padding: 7px 12px 9px; font-size: 10px; opacity: .5;
        border-top: 1px solid var(--qgh-border);
      }
      .qualify-chip { width: 10px; height: 10px; border-radius: 2px; background: var(--qgh-accent); flex-shrink: 0; }
      .state-box { padding: 32px 16px; text-align: center; display: flex; flex-direction: column; align-items: center; gap: 12px; }
      .state-logo { height: 22px; width: auto; filter: var(--qgh-logo-filter); opacity: .45; }
      .state-msg { font-size: 12px; opacity: .45; }
      .sk { border-radius: 4px; background: var(--qgh-border); animation: shimmer 1.4s ease-in-out infinite; }
      .sk-tab { height: 26px; border-radius: 20px; }
      @keyframes shimmer { 0%,100%{opacity:.4} 50%{opacity:.9} }
    `,
  ];

  static properties = {
    apiUrl:       { type: String, attribute: 'api-url' },
    clientKey:    { type: String, attribute: 'client-key' },
    league:       { type: String },
    season:       { type: String },
    title:        { type: String },
    theme:        { type: String },
    refresh:      { type: Number },
    qualifyLabel: { type: String, attribute: 'qualify-label' },
    _standings:   { state: true },
    _activeGroup: { state: true },
    _loading:     { state: true },
    _error:       { state: true },
  };

  constructor() {
    super();
    this._standings   = [];
    this._activeGroup = 0;
    this._loading     = true;
    this._error       = null;
    this.refresh      = 60;
    this.apiUrl       = 'https://api.quienganahoy.com';
  }

  connectedCallback() {
    super.connectedCallback();
    this._load();
    if (this.refresh > 0) {
      this._timer = setInterval(() => this._load(), this.refresh * 1000);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._timer) clearInterval(this._timer);
  }

  async _load() {
    try {
      const data = await fetchStandings(
        this.apiUrl,
        { league: this.league, season: this.season },
        this.clientKey || ''
      );
      this._standings = data[0]?.league?.standings || [];
      this._error = null;
    } catch (e) {
      if (!this._standings.length) this._error = e.message;
    } finally {
      this._loading = false;
    }
  }

  _tabLabel(groupName) {
    return (groupName || '').replace(/^Group\s+/i, 'Grupo ');
  }

  _poweredBy() {
    return html`
      <div class="powered">
        <a href="https://quienganahoy.com/" target="_blank" rel="noopener">
          <span>Powered by</span>
          <img src="https://quienganahoy.com/svg/logo_qgh.svg" alt="QuienGanaHoy">
        </a>
      </div>`;
  }

  render() {
    const title = this.title || 'Tabla de Posiciones';

    if (this._loading) return html`
      <div class="widget">
        <div class="header">
          <div class="header-title">${title}</div>
          <div class="tabs">
            ${[...Array(6)].map(() => html`<div class="sk sk-tab" style="width:62px"></div>`)}
          </div>
        </div>
        <table><tbody>
          ${[...Array(4)].map(() => html`
            <tr><td colspan="10"><div class="sk" style="height:10px;margin:9px 12px"></div></td></tr>`)}
        </tbody></table>
        ${this._poweredBy()}
      </div>`;

    if (this._error) return html`
      <div class="widget">
        <div class="header"><div class="header-title">${title}</div></div>
        <div class="state-box">
          <img class="state-logo" src="https://quienganahoy.com/svg/logo_qgh.svg" alt="">
          <span class="state-msg">No se pudo cargar</span>
        </div>
        ${this._poweredBy()}
      </div>`;

    if (!this._standings.length) return html`
      <div class="widget">
        <div class="header"><div class="header-title">${title}</div></div>
        <div class="state-box"><span class="state-msg">Sin datos disponibles</span></div>
        ${this._poweredBy()}
      </div>`;

    const idx   = Math.min(this._activeGroup, this._standings.length - 1);
    const group = this._standings[idx];
    const anyQ  = group.some(e => e.description?.includes('Promotion'));
    const qLabel = this.qualifyLabel || 'Clasifican a Dieciseisavos de Final';

    return html`
      <div class="widget">
        <div class="header">
          <div class="header-title">${title}</div>
          <div class="tabs">
            ${this._standings.map((g, i) => html`
              <button class="tab ${i === idx ? 'active' : ''}" @click=${() => this._activeGroup = i}>
                ${this._tabLabel(g[0]?.group)}
              </button>`)}
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th class="left">#</th>
              <th class="left">Equipo</th>
              <th>PJ</th><th>G</th><th>E</th><th>P</th>
              <th>GF</th><th>GC</th><th>DG</th><th>PTS</th>
            </tr>
          </thead>
          <tbody>
            ${group.map(e => {
              const q = e.description?.includes('Promotion');
              return html`
                <tr class="${q ? 'qualify' : 'nqualify'}">
                  <td class="rank">${e.rank}</td>
                  <td class="team-col">
                    <div class="team-cell">
                      ${e.team.logo
                        ? html`<img class="team-logo" src="${e.team.logo}" alt="" loading="lazy">`
                        : ''}
                      <span>${translateCountry(e.team.name)}</span>
                    </div>
                  </td>
                  <td>${e.all.played}</td>
                  <td>${e.all.win}</td>
                  <td>${e.all.draw}</td>
                  <td>${e.all.lose}</td>
                  <td>${e.all.goals.for}</td>
                  <td>${e.all.goals.against}</td>
                  <td>${e.goalsDiff > 0 ? '+' : ''}${e.goalsDiff}</td>
                  <td class="pts">${e.points}</td>
                </tr>`;
            })}
          </tbody>
        </table>
        ${anyQ ? html`
          <div class="qualify-note">
            <div class="qualify-chip"></div>
            <span>${qLabel}</span>
          </div>` : ''}
        ${this._poweredBy()}
      </div>`;
  }
}
